import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container, Row, Col, Card, CardBody, CardTitle,
  Badge, Spinner, Table, Progress
} from "reactstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import WelcomeComp from "./WelcomeComp";
import MonthlyEarning from "./MonthlyEarning";
import ActivityComp from "./ActivityComp";
import EngagementStats from "./EngagementStats";
import { fetchPlatformStats } from "../../helpers/platformStats";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";
const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const Dashboard = () => {
  document.title = "Anasayfa | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const isAdmin = authUser?.HESAP_TURU === "Admin";
  const userId = authUser?.ID;

  const fetchOpts = useMemo(() => ({
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }), [token]);

  // ─── STATS STATE ─────────────────────────────────────────────
  const [stats, setStats] = useState({
    totalUsers: 0, newUsersThisMonth: 0, newUsersToday: 0, newUsersYesterday: 0,
    activeListings: 0, totalListings: 0,
    newListingsToday: 0, newListingsYesterday: 0,
    totalOrders: 0, pendingOrders: 0,
    ordersToday: 0, ordersYesterday: 0,
    totalRevenue: 0, monthlyRevenue: 0,
    revenueToday: 0, revenueYesterday: 0,
    pendingOffers: 0, acceptedOffers: 0,
    activeMezats: 0, upcomingMezats: 0, totalStores: 0,
    pendingListings: 0, pendingValuations: 0,
    // non-admin
    myListings: 0, myOrders: 0, myOffers: 0, myRevenue: 0,
    isLoading: true,
  });

  // ─── CHART DATA ───────────────────────────────────────────────
  // ApexCharts boş series ile crash eder (group undefined) — her zaman 12 aylık dizi
  const emptyYear = () => new Array(12).fill(0);
  const [chartSeries, setChartSeries] = useState([
    { name: "Aylık Gelir (₺)", type: "area", data: emptyYear() },
    { name: "Sipariş Sayısı", type: "bar", data: emptyYear() },
    { name: "Yeni İlan", type: "bar", data: emptyYear() },
  ]);
  const [chartLoading, setChartLoading] = useState(true);

  // Cihaz dağılımı: hem AdminDashboard API'si hem OData fallback'i aynı sayıyı
  // göstersin diye tek yerden, sunucu tarafı $count ile hesaplanıyor.
  const [deviceStats, setDeviceStats] = useState({
    android: 0, ios: 0, androidKullanici: 0, iosKullanici: 0,
  });

  // ─── TABLES DATA ──────────────────────────────────────────────
  const [recentOrders, setRecentOrders] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [pendingValList, setPendingValList] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [topCities, setTopCities] = useState([]);
  const [orderStatusDist, setOrderStatusDist] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [myRecentOffers, setMyRecentOffers] = useState([]);
  const [iap, setIap] = useState({
    totalCount: 0, totalCiro: 0, monthlyCiro: 0, todayCount: 0,
    androidCiro: 0, iosCiro: 0, recent: [],
  });

  // ─── FETCH ───────────────────────────────────────────────────
  const fetchDashboard = useCallback(async () => {
    if (!userId) return;
    const year = new Date().getFullYear();
    const yearStart = `${year}-01-01T00:00:00Z`;
    const monthStart = new Date(year, new Date().getMonth(), 1).toISOString();
    const isActiveListing = (l) => (l.AKTIF_DURUM || "").toLowerCase() === "aktif";
    const isDeletedListing = (l) => (l.AKTIF_DURUM || "").toLowerCase() === "silindi";
    const isPendingListing = (l) => (l.AKTIF_DURUM || "").toLowerCase() === "beklemede";

    const isSameDay = (dateVal, ref) => {
      if (!dateVal) return false;
      const d = new Date(dateVal);
      return (
        d.getDate() === ref.getDate() &&
        d.getMonth() === ref.getMonth() &&
        d.getFullYear() === ref.getFullYear()
      );
    };

    const padSeries = (arr) => {
      const base = emptyYear();
      if (!Array.isArray(arr)) return base;
      for (let i = 0; i < 12; i++) base[i] = Number(arr[i]) || 0;
      return base;
    };

    const setAdminChart = (rev, orders, listings) => {
      setChartSeries([
        { name: "Aylık Gelir (₺)", type: "area", data: padSeries(rev) },
        { name: "Sipariş Sayısı", type: "bar", data: padSeries(orders) },
        { name: "Yeni İlan", type: "bar", data: padSeries(listings) },
      ]);
    };

    const applyIapFromStats = (s, recent) => {
      setIap({
        totalCount: s.iapTotalCount || 0,
        totalCiro: Number(s.iapTotalCiro) || 0,
        monthlyCiro: Number(s.iapMonthlyCiro) || 0,
        todayCount: s.iapTodayCount || 0,
        androidCiro: Number(s.iapAndroidCiro) || 0,
        iosCiro: Number(s.iapIosCiro) || 0,
        recent: recent || [],
      });
    };

    const applyIapFromRows = (rows) => {
      const now = new Date();
      const monthStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const plat = (p) => (p || "").toLowerCase();
      const totalCiro = rows.reduce((s, r) => s + (Number(r.TUTAR) || 0), 0);
      setIap({
        totalCount: rows.length,
        totalCiro,
        monthlyCiro: rows
          .filter((r) => r.TARIH && new Date(r.TARIH) >= monthStartDate)
          .reduce((s, r) => s + (Number(r.TUTAR) || 0), 0),
        todayCount: rows.filter((r) => isSameDay(r.TARIH, now)).length,
        androidCiro: rows
          .filter((r) => plat(r.PLATFORM) === "android")
          .reduce((s, r) => s + (Number(r.TUTAR) || 0), 0),
        iosCiro: rows
          .filter((r) => plat(r.PLATFORM) === "ios" || plat(r.PLATFORM) === "apple")
          .reduce((s, r) => s + (Number(r.TUTAR) || 0), 0),
        recent: [...rows]
          .sort((a, b) => new Date(b.TARIH || 0) - new Date(a.TARIH || 0))
          .slice(0, 8),
      });
    };

    const fetchIapViaOData = async () => {
      try {
        const r = await fetch(
          `${BASE_URL}/SATIN_ALMA_GECMISI?$orderby=TARIH desc&$top=5000` +
            `&$expand=KULLANICILAR($select=AD_SOYAD),PAKETLER($select=AD,TIP)`,
          fetchOpts
        );
        if (!r.ok) return;
        const data = await r.json();
        applyIapFromRows(data.value || []);
      } catch { /* kart 0 kalır */ }
    };

    /** OData fallback — AdminDashboard API henüz deploy edilmemişse */
    const fetchAdminViaOData = async () => {
      const safe = async (p) => {
        try {
          const r = await p;
          if (!r || !r.ok) return null;
          return await r.json();
        } catch {
          return null;
        }
      };

      const [
        usersData, storesData, listingsData, ordersData,
        offersData, mezatData, odemeData,
        recentOrdersData, newUsersData, odemeMonthlyData,
        recentPaymentsData,
      ] = await Promise.all([
        safe(fetch(`${BASE_URL}/KULLANICILAR?$select=ID,HESAP_TURU,OLUSTURMA_TARIHI&$top=50000`, fetchOpts)),
        safe(fetch(`${BASE_URL}/MAGAZALAR?$select=ID&$top=10000`, fetchOpts)),
        safe(fetch(`${BASE_URL}/ILANLAR?$select=ID,AKTIF_DURUM,KATEGORI_ID,KONUM_IL,OLUSTURMA_TARIHI,DEGERLEMEYE_ACIK_MI&$top=50000`, fetchOpts)),
        safe(fetch(`${BASE_URL}/SIPARIS?$select=ID,SIPARIS_DURUMU,TOPLAM_TUTAR,SIPARIS_TARIHI&$top=50000`, fetchOpts)),
        safe(fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$select=ID,DURUM&$top=50000`, fetchOpts)),
        safe(fetch(`${BASE_URL}/MEZATLAR?$select=ID,BITIS_TARIHI,TEKLIF_ACIK,DURUM_ID&$top=10000`, fetchOpts)),
        safe(fetch(`${BASE_URL}/ODEME?$select=ID,ODEME_MIKTARI,ODEME_TARIHI,ODEME_DURUMU&$top=50000`, fetchOpts)),
        safe(fetch(`${BASE_URL}/SIPARIS?$top=8&$orderby=SIPARIS_TARIHI desc&$expand=KULLANICILAR($select=AD_SOYAD)`, fetchOpts)),
        safe(fetch(`${BASE_URL}/KULLANICILAR?$top=6&$orderby=ID desc&$select=ID,AD_SOYAD,HESAP_TURU,OLUSTURMA_TARIHI,Mail_Adress`, fetchOpts)),
        safe(fetch(`${BASE_URL}/ODEME?$select=ODEME_MIKTARI,ODEME_TARIHI&$filter=ODEME_TARIHI ge ${yearStart}&$top=50000`, fetchOpts)),
        safe(fetch(`${BASE_URL}/ODEME?$top=8&$orderby=ODEME_TARIHI desc&$expand=SIPARIS($expand=KULLANICILAR($select=AD_SOYAD))`, fetchOpts)),
      ]);

      const users = usersData?.value || [];
      const listings = listingsData?.value || [];
      const orders = ordersData?.value || [];
      const offers = offersData?.value || [];
      const odeme = odemeData?.value || [];
      const mezats = mezatData?.value || [];
      const liveListings = listings.filter((l) => !isDeletedListing(l));

      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);

      const newUsersMonth = users.filter((u) => {
        if (!u.OLUSTURMA_TARIHI) return false;
        const d = new Date(u.OLUSTURMA_TARIHI);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length;

      const totalRevenue = odeme.reduce((s, o) => s + (Number(o.ODEME_MIKTARI) || 0), 0);
      const monthlyRev = odeme
        .filter((o) => o.ODEME_TARIHI && new Date(o.ODEME_TARIHI) >= new Date(monthStart))
        .reduce((s, o) => s + (Number(o.ODEME_MIKTARI) || 0), 0);

      setStats({
        totalUsers: users.length,
        newUsersThisMonth: newUsersMonth,
        newUsersToday: users.filter((u) => isSameDay(u.OLUSTURMA_TARIHI, now)).length,
        newUsersYesterday: users.filter((u) => isSameDay(u.OLUSTURMA_TARIHI, yesterday)).length,
        activeListings: liveListings.filter(isActiveListing).length,
        totalListings: liveListings.length,
        newListingsToday: liveListings.filter((l) => isSameDay(l.OLUSTURMA_TARIHI, now)).length,
        newListingsYesterday: liveListings.filter((l) => isSameDay(l.OLUSTURMA_TARIHI, yesterday)).length,
        totalOrders: orders.length,
        pendingOrders: orders.filter((o) => o.SIPARIS_DURUMU === "Bekliyor").length,
        ordersToday: orders.filter((o) => isSameDay(o.SIPARIS_TARIHI, now)).length,
        ordersYesterday: orders.filter((o) => isSameDay(o.SIPARIS_TARIHI, yesterday)).length,
        totalRevenue,
        monthlyRevenue: monthlyRev,
        revenueToday: odeme
          .filter((o) => isSameDay(o.ODEME_TARIHI, now))
          .reduce((s, o) => s + (Number(o.ODEME_MIKTARI) || 0), 0),
        revenueYesterday: odeme
          .filter((o) => isSameDay(o.ODEME_TARIHI, yesterday))
          .reduce((s, o) => s + (Number(o.ODEME_MIKTARI) || 0), 0),
        pendingOffers: offers.filter((o) => o.DURUM === 0 || o.DURUM === null).length,
        acceptedOffers: offers.filter((o) => o.DURUM === 1).length,
        activeMezats: mezats.filter((m) => m.TEKLIF_ACIK).length,
        upcomingMezats: mezats.filter((m) => m.DURUM_ID === 1).length,
        totalStores: storesData?.value?.length || 0,
        pendingListings: liveListings.filter(isPendingListing).length,
        pendingValuations: liveListings.filter(
          (l) => isPendingListing(l) && l.DEGERLEMEYE_ACIK_MI
        ).length,
        isLoading: false,
      });

      setRecentOrders(recentOrdersData?.value || []);
      setNewUsers(newUsersData?.value || []);
      setRecentPayments(recentPaymentsData?.value || []);

      // Top kategoriler / şehirler — silinen ilanlar hariç. Kategori ve il
      // alanları zaten yukarıdaki ILANLAR sorgusunda geldiği için ek istek yok.
      const RENKLER = ["primary", "success", "warning", "info", "danger"];

      const katCount = {};
      liveListings.forEach((l) => {
        if (l.KATEGORI_ID) katCount[l.KATEGORI_ID] = (katCount[l.KATEGORI_ID] || 0) + 1;
      });
      const totalCatIlan = Object.values(katCount).reduce((a, b) => a + b, 0);
      const topKatIds = Object.entries(katCount)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([id, cnt]) => ({ id: parseInt(id, 10), cnt }));

      if (topKatIds.length) {
        const katData = await safe(
          fetch(
            `${BASE_URL}/KATEGORILER?$filter=ID in (${topKatIds.map((k) => k.id).join(",")})&$select=ID,AD`,
            fetchOpts
          )
        );
        const nameMap = {};
        (katData?.value || []).forEach((k) => { nameMap[k.ID] = k.AD; });
        setTopCategories(
          topKatIds.map((k, i) => ({
            name: nameMap[k.id] || `Kategori #${k.id}`,
            count: k.cnt,
            percent: totalCatIlan ? Math.round((k.cnt / totalCatIlan) * 100) : 0,
            color: RENKLER[i],
          }))
        );
      } else {
        setTopCategories([]);
      }

      const ilCount = {};
      let totalIl = 0;
      liveListings.forEach((l) => {
        if (l.KONUM_IL) {
          const il = l.KONUM_IL.trim().toLocaleUpperCase("tr-TR");
          ilCount[il] = (ilCount[il] || 0) + 1;
          totalIl++;
        }
      });
      setTopCities(
        Object.entries(ilCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([name, count], i) => ({
            name,
            count,
            percent: totalIl ? Math.round((count / totalIl) * 100) : 0,
            color: RENKLER[i],
          }))
      );

      const monthlyRevArr = emptyYear();
      const monthlyOrderArr = emptyYear();
      const monthlyIlanArr = emptyYear();
      (odemeMonthlyData?.value || odeme).forEach((o) => {
        if (o.ODEME_TARIHI) {
          const d = new Date(o.ODEME_TARIHI);
          if (d.getFullYear() === year) monthlyRevArr[d.getMonth()] += Number(o.ODEME_MIKTARI) || 0;
        }
      });
      orders.forEach((o) => {
        if (o.SIPARIS_TARIHI) {
          const d = new Date(o.SIPARIS_TARIHI);
          if (d.getFullYear() === year) monthlyOrderArr[d.getMonth()]++;
        }
      });
      liveListings.forEach((l) => {
        if (l.OLUSTURMA_TARIHI) {
          const d = new Date(l.OLUSTURMA_TARIHI);
          if (d.getFullYear() === year) monthlyIlanArr[d.getMonth()]++;
        }
      });
      setAdminChart(
        monthlyRevArr.map((v) => Math.round(v)),
        monthlyOrderArr,
        monthlyIlanArr
      );

      // Opsiyonel listeler (hata olursa sessiz)
      try {
        const pvFilter = encodeURIComponent(
          "AKTIF_DURUM eq 'Beklemede' and DEGERLEMEYE_ACIK_MI eq true"
        );
        const pvRes = await fetch(
          `${BASE_URL}/ILANLAR?$filter=${pvFilter}&$top=6&$orderby=OLUSTURMA_TARIHI desc` +
            `&$select=ID,AD,OLUSTURMA_TARIHI&$expand=KULLANICILAR($select=AD_SOYAD)`,
          fetchOpts
        );
        if (pvRes.ok) {
          const pvData = await pvRes.json();
          setPendingValList(pvData?.value || []);
        }
      } catch { /* ignore */ }

      const statusMap = {};
      orders.forEach((o) => {
        const s = o.SIPARIS_DURUMU || "Bilinmiyor";
        statusMap[s] = (statusMap[s] || 0) + 1;
      });
      setOrderStatusDist(
        Object.entries(statusMap)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([name, count]) => ({
            name,
            count,
            percent: orders.length ? Math.round((count / orders.length) * 100) : 0,
          }))
      );
    };

    try {
      if (isAdmin) {
        // Önce yeni API; 404/deploy yoksa OData fallback
        let usedApi = false;
        try {
          const res = await fetch(`${BASE_URL}/api/AdminDashboard/Ozet`, fetchOpts);
          if (res.ok) {
            const data = await res.json();
            const s = data.stats || {};
            setStats({
              totalUsers: s.totalUsers || 0,
              newUsersThisMonth: s.newUsersThisMonth || 0,
              newUsersToday: s.newUsersToday || 0,
              newUsersYesterday: s.newUsersYesterday || 0,
              activeListings: s.activeListings || 0,
              totalListings: s.totalListings || 0,
              newListingsToday: s.newListingsToday || 0,
              newListingsYesterday: s.newListingsYesterday || 0,
              totalOrders: s.totalOrders || 0,
              pendingOrders: s.pendingOrders || 0,
              ordersToday: s.ordersToday || 0,
              ordersYesterday: s.ordersYesterday || 0,
              totalRevenue: Number(s.totalRevenue) || 0,
              monthlyRevenue: Number(s.monthlyRevenue) || 0,
              revenueToday: Number(s.revenueToday) || 0,
              revenueYesterday: Number(s.revenueYesterday) || 0,
              pendingOffers: s.pendingOffers || 0,
              acceptedOffers: s.acceptedOffers || 0,
              activeMezats: s.activeMezats || 0,
              upcomingMezats: s.upcomingMezats || 0,
              totalStores: s.totalStores || 0,
              pendingListings: s.pendingListings || 0,
              pendingValuations: s.pendingValuations || 0,
              isLoading: false,
            });
            setRecentOrders(data.recentOrders || []);
            setNewUsers(data.newUsers || []);
            setPendingValList(data.pendingValuationsList || []);
            setTopCategories(data.topCategories || []);
            setTopCities(data.topCities || []);
            setOrderStatusDist(data.orderStatusDist || []);
            setRecentPayments(data.recentPayments || []);
            const chart = data.chart || {};
            setAdminChart(chart.monthlyRevenue, chart.monthlyOrders, chart.monthlyListings);
            if (s.iapTotalCount != null || (data.recentIap && data.recentIap.length)) {
              applyIapFromStats(s, data.recentIap);
            } else {
              await fetchIapViaOData();
            }
            usedApi = true;
          }
        } catch (apiErr) {
          console.warn("AdminDashboard API kullanılamadı, OData fallback:", apiErr);
        }
        if (!usedApi) {
          await fetchAdminViaOData();
          await fetchIapViaOData();
        }
      } else {
        // ── NON-ADMIN ───────────────────────────────────────
        const userFilter = `KIMDEN_ID eq ${userId}`;

        const [listRes, ordRes, offerRes, odemeRes] = await Promise.allSettled([
          fetch(`${BASE_URL}/ILANLAR?$filter=${userFilter}&$select=ID,AKTIF_DURUM,OLUSTURMA_TARIHI`, fetchOpts),
          fetch(`${BASE_URL}/SIPARIS?$filter=SATICI_ID eq ${userId}&$select=ID,SIPARIS_DURUMU,TOPLAM_TUTAR,SIPARIS_TARIHI&$top=500`, fetchOpts),
          fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$filter=KULLANICI_ID eq ${userId}&$select=ID,DURUM,TEKLIF_TUTARI,TEKLIF_TARIHI&$top=8&$orderby=TEKLIF_TARIHI desc`, fetchOpts),
          fetch(`${BASE_URL}/ODEME?$top=8&$orderby=ODEME_TARIHI desc&$expand=SIPARIS`, fetchOpts),
        ]);

        const safe2 = async (res) => {
          if (res.status !== "fulfilled") return null;
          try { return await res.value.json(); } catch { return null; }
        };
        const [listData, ordData, offerData, odemeData2] = await Promise.all([
          safe2(listRes), safe2(ordRes), safe2(offerRes), safe2(odemeRes),
        ]);

        const myListings = listData?.value || [];
        const myOrders = ordData?.value || [];
        const myOffers = offerData?.value || [];
        const myOdeme = odemeData2?.value || [];

        const liveMyListings = myListings.filter((l) => !isDeletedListing(l));
        const myRevenue = myOrders.reduce((s, o) => s + (Number(o.TOPLAM_TUTAR) || 0), 0);

        const monthlyArr = emptyYear();
        liveMyListings.forEach((l) => {
          if (l.OLUSTURMA_TARIHI) {
            const d = new Date(l.OLUSTURMA_TARIHI);
            if (d.getFullYear() === year) monthlyArr[d.getMonth()]++;
          }
        });
        setChartSeries([{ name: "Eklenen İlanlar", type: "bar", data: monthlyArr }]);

        setStats({
          myListings: liveMyListings.length,
          activeListings: liveMyListings.filter(isActiveListing).length,
          myOrders: myOrders.length,
          pendingOrders: myOrders.filter((o) => o.SIPARIS_DURUMU === "Bekliyor").length,
          myOffers: myOffers.length,
          pendingOffers: myOffers.filter((o) => o.DURUM === 0 || o.DURUM === null).length,
          myRevenue,
          isLoading: false,
        });

        setMyRecentOffers(myOffers);
        setRecentPayments(myOdeme);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      // Crash önleme: grafik boş kalmasın
      if (isAdmin) setAdminChart(emptyYear(), emptyYear(), emptyYear());
      else setChartSeries([{ name: "Eklenen İlanlar", type: "bar", data: emptyYear() }]);
      setStats((prev) => ({ ...prev, isLoading: false }));
    } finally {
      setChartLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isAdmin, fetchOpts]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    if (!isAdmin) return;
    let iptal = false;
    fetchPlatformStats(fetchOpts)
      .then((s) => {
        if (iptal) return;
        setDeviceStats({
          android: s.android,
          ios: s.ios,
          androidKullanici: s.androidKullanici,
          iosKullanici: s.iosKullanici,
        });
      })
      .catch(() => { /* kart 0 kalır */ });
    return () => { iptal = true; };
  }, [isAdmin, fetchOpts]);

  // ─── CHART OPTIONS ────────────────────────────────────────────
  const chartOptions = useMemo(() => ({
    chart: {
      id: isAdmin ? "admin-dashboard-year" : "user-dashboard-year",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
    },
    stroke: { width: isAdmin ? [2, 0, 0] : [0], curve: "smooth" },
    fill: {
      type: isAdmin ? ["gradient", "solid", "solid"] : ["solid"],
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: MONTHS,
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: isAdmin
      ? [
          {
            seriesName: "Aylık Gelir (₺)",
            title: { text: "Gelir (₺)" },
            labels: {
              formatter: (v) => `${Math.round(Number(v) || 0).toLocaleString("tr-TR")}₺`,
            },
          },
          {
            seriesName: "Sipariş Sayısı",
            opposite: true,
            show: false,
          },
          {
            seriesName: "Yeni İlan",
            opposite: true,
            show: false,
          },
        ]
      : [{ title: { text: "İlan" } }],
    colors: isAdmin ? ["#556ee6", "#34c38f", "#f1b44c"] : ["#556ee6"],
    legend: { position: "top" },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val, opts) => {
          const n = Number(val);
          if (!Number.isFinite(n)) return "-";
          const idx = opts && typeof opts.seriesIndex === "number" ? opts.seriesIndex : 0;
          if (isAdmin && idx === 0) return `${n.toLocaleString("tr-TR")} ₺`;
          return `${n} adet`;
        },
      },
    },
    plotOptions: { bar: { columnWidth: "30%", borderRadius: 3 } },
    grid: { borderColor: "#f1f1f1" },
  }), [isAdmin]);

  const chartReady =
    !chartLoading &&
    Array.isArray(chartSeries) &&
    chartSeries.length > 0 &&
    chartSeries.every((s) => s && Array.isArray(s.data) && s.data.length === 12);

  // ─── HELPERS ──────────────────────────────────────────────────
  const statusBadge = (s) => {
    const map = {
      "Teslim Edildi": "success", "Bekliyor": "warning", "İptal": "danger",
      "Onaylandı": "info", "Hazırlanıyor": "primary", "Kargoya Verildi": "secondary",
    };
    return <Badge color={map[s] || "light"} className="text-dark">{s || "-"}</Badge>;
  };

  const hesapBadge = (h) => {
    const map = { Admin: "danger", Satıcı: "primary", Alıcı: "success" };
    return <Badge color={map[h] || "secondary"}>{h}</Badge>;
  };

  const teklifBadge = (d) => {
    if (d === 1) return <Badge color="success">Kabul</Badge>;
    if (d === 2) return <Badge color="danger">Red</Badge>;
    return <Badge color="warning">Bekliyor</Badge>;
  };

  // ─── RENDER ───────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Özet Ekranı" />

          {/* ── ADMIN DASHBOARD ───────────────────────────────── */}
          {isAdmin ? (
            <>
              {/* GÜNLÜK ÖZET (Dün / Bugün) */}
              <Row className="mb-2">
                <Col xs={12}>
                  <p className="text-muted text-uppercase fw-semibold font-size-11 mb-2 letter-spacing-1">
                    Günlük Özet
                  </p>
                </Col>
                {[
                  {
                    title: "Dün Üye Olan",
                    value: stats.newUsersYesterday,
                    sub: (() => {
                      const d = new Date();
                      d.setDate(d.getDate() - 1);
                      return d.toLocaleDateString("tr-TR");
                    })(),
                    icon: "bx-user-plus",
                    color: stats.newUsersYesterday > 0 ? "primary" : "secondary",
                    link: "/contacts-list",
                  },
                  {
                    title: "Bugün Üye Olan",
                    value: stats.newUsersToday,
                    sub: new Date().toLocaleDateString("tr-TR"),
                    icon: "bx-user-check",
                    color: stats.newUsersToday > 0 ? "success" : "secondary",
                    link: "/contacts-list",
                  },
                  {
                    title: "Dün Açılan İlan",
                    value: stats.newListingsYesterday,
                    sub: (() => {
                      const d = new Date();
                      d.setDate(d.getDate() - 1);
                      return d.toLocaleDateString("tr-TR");
                    })(),
                    icon: "bx-store",
                    color: stats.newListingsYesterday > 0 ? "info" : "secondary",
                    link: "/ilan-yonetimi",
                  },
                  {
                    title: "Bugün Açılan İlan",
                    value: stats.newListingsToday,
                    sub: new Date().toLocaleDateString("tr-TR"),
                    icon: "bx-store-alt",
                    color: stats.newListingsToday > 0 ? "success" : "secondary",
                    link: "/ilan-yonetimi",
                  },
                ].map((s, i) => (
                  <Col key={`daily-${i}`} xs={6} md={3} xl={3}>
                    <Card className="mini-stats-wid mb-3 border border-light">
                      <CardBody className="py-3 px-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1 overflow-hidden">
                            <p className="text-muted fw-medium mb-1 font-size-12 text-truncate">{s.title}</p>
                            <h5 className="mb-0 font-size-16">
                              {stats.isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                            </h5>
                            <p className="text-muted mb-0 font-size-11 mt-1">{s.sub}</p>
                          </div>
                          <div className={`avatar-sm rounded-circle bg-${s.color} ms-2 flex-shrink-0`}>
                            <span className={`avatar-title rounded-circle bg-${s.color}`}>
                              <i className={`bx ${s.icon} font-size-18`} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* GENEL STAT KARTLARI */}
              <Row className="mb-2">
                {[
                  {
                    title: "Toplam Kullanıcı",
                    value: stats.totalUsers,
                    sub: `+${stats.newUsersThisMonth} bu ay`,
                    icon: "bx-group", color: "primary",
                    link: "/contacts-list",
                  },
                  {
                    title: "Android Cihaz",
                    value: deviceStats.android,
                    sub: `${deviceStats.androidKullanici} kullanıcı`,
                    icon: "bxl-android", color: "success",
                    link: "/fcm-tokenlari",
                  },
                  {
                    title: "iOS Cihaz",
                    value: deviceStats.ios,
                    sub: `${deviceStats.iosKullanici} kullanıcı`,
                    icon: "bxl-apple", color: "dark",
                    link: "/fcm-tokenlari",
                  },
                  {
                    title: "Aktif İlanlar",
                    value: stats.activeListings,
                    sub: `${stats.totalListings} toplam`,
                    icon: "bx-store-alt", color: "success",
                    link: "/ilan-yonetimi",
                  },
                  {
                    title: "Onay Bekleyen",
                    value: stats.pendingListings,
                    sub: `${stats.pendingValuations} değerleme`,
                    icon: "bx-time-five",
                    color: stats.pendingListings > 0 ? "warning" : "secondary",
                    link: "/ilan-yonetimi",
                  },
                  {
                    title: "Toplam Sipariş",
                    value: stats.totalOrders,
                    sub: `${stats.pendingOrders} bekliyor`,
                    icon: "bx-cart", color: "warning",
                    link: "/siparis-yonetimi",
                  },
                  {
                    title: "Toplam Gelir",
                    value: `${stats.totalRevenue.toLocaleString("tr-TR")} ₺`,
                    sub: `${stats.monthlyRevenue.toLocaleString("tr-TR")} ₺ bu ay`,
                    icon: "bx-money", color: "info",
                    link: "/crypto-wallet",
                  },
                  {
                    title: "Uygulama İçi Satın Alma",
                    value: `${iap.totalCiro.toLocaleString("tr-TR")} ₺`,
                    sub: `${iap.totalCount} işlem · ${iap.monthlyCiro.toLocaleString("tr-TR")} ₺ bu ay`,
                    icon: "bx-purchase-tag", color: "success",
                    link: "/satin-alma-gecmisi",
                  },
                  {
                    title: "Bekleyen Teklifler",
                    value: stats.pendingOffers,
                    sub: `${stats.acceptedOffers} kabul edildi`,
                    icon: "bx-message-square-dots", color: "danger",
                    link: "/ilan-yonetimi",
                  },
                  {
                    title: "Başlayacak Mezat",
                    value: stats.upcomingMezats,
                    sub: `${stats.activeMezats} canlı`,
                    icon: "bx-time-five",
                    color: stats.upcomingMezats > 0 ? "info" : "secondary",
                    link: "/mezat-yonetimi",
                  },
                  {
                    title: "Mağazalar",
                    value: stats.totalStores,
                    sub: `${stats.activeMezats} aktif mezat`,
                    icon: "bx-buildings", color: "secondary",
                    link: "/magaza-yonetimi",
                  },
                ].map((s, i) => (
                  <Col key={i} xs={6} md={4} xl={2}>
                    <Card className="mini-stats-wid mb-3">
                      <CardBody className="py-3 px-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1 overflow-hidden">
                            <p className="text-muted fw-medium mb-1 font-size-12 text-truncate">{s.title}</p>
                            <h5 className="mb-0 font-size-16">
                              {stats.isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                            </h5>
                            <p className="text-muted mb-0 font-size-11 mt-1">{s.sub}</p>
                          </div>
                          <div className={`avatar-sm rounded-circle bg-${s.color} ms-2 flex-shrink-0`}>
                            <span className={`avatar-title rounded-circle bg-${s.color}`}>
                              <i className={`bx ${s.icon} font-size-18`} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* GRAFİK + SİPARİŞ DURUM */}
              <Row>
                <Col xl={8}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <CardTitle className="mb-0">{new Date().getFullYear()} Yılı Özeti</CardTitle>
                          <p className="text-muted font-size-12 mb-0">Aylık gelir, sipariş ve ilan trendi</p>
                        </div>
                      </div>
                      {!chartReady ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <ReactApexChart
                          key={`admin-chart-${chartSeries.map((s) => s.data.join(",")).join("|").slice(0, 80)}`}
                          options={chartOptions}
                          series={chartSeries}
                          type="line"
                          height={300}
                          className="apex-charts"
                        />
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={4}>
                  <Card className="h-100">
                    <CardBody>
                      <CardTitle className="mb-3">Sipariş Durum Dağılımı</CardTitle>
                      {orderStatusDist.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="bx bx-cart display-4 opacity-25 d-block mb-2" />
                          Henüz sipariş yok
                        </div>
                      ) : (
                        <div>
                          {orderStatusDist.map((s, i) => (
                            <div key={i} className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <span className="font-size-13">{s.name}</span>
                                <span className="fw-bold">{s.count}</span>
                              </div>
                              <Progress
                                value={s.percent}
                                color={["primary", "success", "warning", "danger", "info"][i]}
                                className="progress-sm"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* ONAY BEKLEYEN DEĞERLEMELER */}
              {pendingValList.length > 0 && (
                <Row>
                  <Col xl={12}>
                    <Card className="border border-warning">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <CardTitle className="mb-0">
                            <i className="bx bx-time-five text-warning me-1" />
                            Onay Bekleyen Değerlemeler
                          </CardTitle>
                          <Link to="/ilan-yonetimi" className="text-primary font-size-13">
                            Tümünü Gör <i className="mdi mdi-arrow-right" />
                          </Link>
                        </div>
                        <div className="table-responsive">
                          <Table className="table-hover align-middle mb-0" size="sm">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>İlan</th>
                                <th>Sahibi</th>
                                <th>Tarih</th>
                                <th className="text-end">İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pendingValList.map(v => (
                                <tr key={v.ID}>
                                  <td className="fw-medium text-muted">#{v.ID}</td>
                                  <td>{v.AD || "-"}</td>
                                  <td>{v.KULLANICILAR?.AD_SOYAD || "-"}</td>
                                  <td className="text-muted font-size-12">
                                    {v.OLUSTURMA_TARIHI ? new Date(v.OLUSTURMA_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                  </td>
                                  <td className="text-end">
                                    <Link to="/ilan-yonetimi" className="btn btn-sm btn-outline-primary">
                                      İncele
                                    </Link>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* SON SİPARİŞLER + YENİ ÜYELER */}
              <Row>
                <Col xl={7}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">Son Siparişler</CardTitle>
                        <Link to="/siparis-yonetimi" className="text-primary font-size-13">
                          Tümünü Gör <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Müşteri</th>
                              <th>Tutar</th>
                              <th>Tarih</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentOrders.length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-3">Sipariş bulunamadı</td></tr>
                            ) : recentOrders.map(o => (
                              <tr key={o.ID}>
                                <td className="fw-medium text-muted">#{o.ID}</td>
                                <td>{o.KULLANICILAR?.AD_SOYAD || "-"}</td>
                                <td className="fw-bold text-primary">
                                  {o.TOPLAM_TUTAR ? `${Number(o.TOPLAM_TUTAR).toLocaleString("tr-TR")} ₺` : "-"}
                                </td>
                                <td className="text-muted font-size-12">
                                  {o.SIPARIS_TARIHI ? new Date(o.SIPARIS_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                                <td>{statusBadge(o.SIPARIS_DURUMU)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={5}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">Yeni Üyeler</CardTitle>
                        <Link to="/contacts-list" className="text-primary font-size-13">
                          Tümünü Gör <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Ad Soyad</th>
                              <th>Tip</th>
                              <th>Tarih</th>
                            </tr>
                          </thead>
                          <tbody>
                            {newUsers.length === 0 ? (
                              <tr><td colSpan={3} className="text-center text-muted py-3">Kayıt yok</td></tr>
                            ) : newUsers.map(u => (
                              <tr key={u.ID}>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="avatar-xs rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center"
                                    >
                                      <span className="text-primary fw-bold font-size-11">
                                        {u.AD_SOYAD?.charAt(0) || "?"}
                                      </span>
                                    </div>
                                    <div>
                                      <div className="fw-medium font-size-13">{u.AD_SOYAD}</div>
                                      <div className="text-muted font-size-11">{u.Mail_Adress}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>{hesapBadge(u.HESAP_TURU)}</td>
                                <td className="text-muted font-size-12">
                                  {u.OLUSTURMA_TARIHI ? (
                                    <>
                                      <div>{new Date(u.OLUSTURMA_TARIHI).toLocaleDateString("tr-TR")}</div>
                                      <div className="font-size-11">
                                        <i className="bx bx-time-five me-1" />
                                        {new Date(u.OLUSTURMA_TARIHI).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                                      </div>
                                    </>
                                  ) : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* TOP KATEGORİLER + ŞEHİRLER */}
              <Row>
                <Col xl={6}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">En Çok İlan Açılan Kategoriler</CardTitle>
                        <Link to="/kategori-yonetimi" className="text-primary font-size-13">
                          Yönet <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      {topCategories.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="bx bx-category display-4 opacity-25 d-block mb-2" />Veri yok
                        </div>
                      ) : topCategories.map((c, i) => (
                        <div key={i} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="font-size-13 fw-medium">{c.name}</span>
                            <span className="text-muted font-size-12">{c.count} ilan · %{c.percent}</span>
                          </div>
                          <Progress value={c.percent} color={c.color} className="progress-sm" />
                        </div>
                      ))}
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={6}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-3">İlan Yoğunluğuna Göre Şehirler</CardTitle>
                      {topCities.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <i className="bx bx-map display-4 opacity-25 d-block mb-2" />Konum verisi yok
                        </div>
                      ) : (
                        <>
                          <div className="text-center mb-3">
                            <i className="bx bx-map-pin text-primary" style={{ fontSize: 40 }} />
                            <h4 className="mb-0">{topCities[0]?.count} İlan</h4>
                            <p className="text-uppercase text-muted fw-medium">{topCities[0]?.name}</p>
                          </div>
                          <Table size="sm" className="align-middle mb-0">
                            <tbody>
                              {topCities.slice(1).map((c, i) => (
                                <tr key={i}>
                                  <td className="fw-medium text-uppercase" style={{ width: "35%" }}>{c.name}</td>
                                  <td style={{ width: "20%" }}><h6 className="mb-0">{c.count}</h6></td>
                                  <td>
                                    <Progress value={c.percent} color={c.color} className="progress-sm" />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* KULLANICI ETKİLEŞİM ÖZETİ */}
              <div className="mt-2 mb-1">
                <h5 className="font-size-15 mb-0">
                  <i className="bx bx-pulse text-primary me-1" />
                  Kullanıcı Etkileşim Özeti
                </h5>
                <p className="text-muted font-size-12 mb-2">Mobil uygulamadaki takip, favori, beğeni ve sepet aktivitesi</p>
              </div>
              <EngagementStats />

              {/* SON ÖDEMELER + UYGULAMA İÇİ SATIN ALMA */}
              <Row>
                <Col lg={6}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">Son Ödemeler</CardTitle>
                        <Link to="/crypto-wallet" className="text-primary font-size-13">
                          Finansa Git <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Referans No</th>
                              <th>Müşteri</th>
                              <th>Tutar</th>
                              <th>Ödeme Tipi</th>
                              <th>Tarih</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentPayments.length === 0 ? (
                              <tr><td colSpan={6} className="text-center text-muted py-3">Ödeme kaydı yok</td></tr>
                            ) : recentPayments.map(p => (
                              <tr key={p.ID}>
                                <td className="fw-medium text-muted">
                                  {p.REFERANS_NO || `#${p.ID}`}
                                </td>
                                <td>{p.SIPARIS?.KULLANICILAR?.AD_SOYAD || "-"}</td>
                                <td className="fw-bold text-success">
                                  {Number(p.ODEME_MIKTARI).toLocaleString("tr-TR")} ₺
                                </td>
                                <td><Badge color="light" className="text-dark border">{p.ODEME_TIP || "-"}</Badge></td>
                                <td className="text-muted font-size-12">
                                  {p.ODEME_TARIHI ? new Date(p.ODEME_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                                <td>
                                  <Badge color={
                                    p.ODEME_DURUMU?.toLowerCase().includes("tamamland") ? "success"
                                    : p.ODEME_DURUMU?.toLowerCase().includes("iptal") ? "danger"
                                    : "warning"
                                  }>
                                    {p.ODEME_DURUMU || "Bekliyor"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col lg={6}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <CardTitle className="mb-0">Uygulama İçi Satın Almalar</CardTitle>
                          <p className="text-muted font-size-12 mb-0">
                            iOS {iap.iosCiro.toLocaleString("tr-TR")} ₺ · Android {iap.androidCiro.toLocaleString("tr-TR")} ₺
                          </p>
                        </div>
                        <Link to="/satin-alma-gecmisi" className="text-primary font-size-13">
                          Tümünü Gör <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Kullanıcı</th>
                              <th>Paket</th>
                              <th>Tutar</th>
                              <th>Platform</th>
                              <th>Tarih</th>
                            </tr>
                          </thead>
                          <tbody>
                            {iap.recent.length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-3">Satın alma kaydı yok</td></tr>
                            ) : iap.recent.map((p) => (
                              <tr key={p.ID}>
                                <td>{p.KULLANICILAR?.AD_SOYAD || `#${p.KULLANICI_ID || p.ID}`}</td>
                                <td>
                                  <Badge color="light" className="text-dark">
                                    {p.PAKETLER?.AD || p.SKU || "—"}
                                  </Badge>
                                </td>
                                <td className="fw-bold text-success">
                                  {Number(p.TUTAR || 0).toLocaleString("tr-TR")} ₺
                                </td>
                                <td>
                                  {(p.PLATFORM || "").toLowerCase().includes("android")
                                    ? <Badge color="success">Android</Badge>
                                    : (p.PLATFORM || "").toLowerCase().includes("ios") || (p.PLATFORM || "").toLowerCase().includes("apple")
                                      ? <Badge color="dark">iOS</Badge>
                                      : <Badge color="secondary">{p.PLATFORM || "—"}</Badge>}
                                </td>
                                <td className="text-muted font-size-12">
                                  {p.TARIH ? new Date(p.TARIH).toLocaleDateString("tr-TR") : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            /* ── NON-ADMIN DASHBOARD ──────────────────────────── */
            <>
              {/* STAT KARTLARI */}
              <Row className="mb-2">
                {[
                  {
                    title: "İlanlarım",
                    value: stats.myListings,
                    sub: `${stats.activeListings} aktif`,
                    icon: "bx-store-alt", color: "primary",
                    link: "/job-list",
                  },
                  {
                    title: "Siparişlerim",
                    value: stats.myOrders,
                    sub: `${stats.pendingOrders} bekliyor`,
                    icon: "bx-cart", color: "success",
                    link: "/job-apply",
                  },
                  {
                    title: "Tekliflerim",
                    value: stats.myOffers,
                    sub: `${stats.pendingOffers} bekliyor`,
                    icon: "bx-message-square-dots", color: "warning",
                    link: "/ecommerce-products",
                  },
                  {
                    title: "Toplam Kazanç",
                    value: `${(stats.myRevenue || 0).toLocaleString("tr-TR")} ₺`,
                    sub: "sipariş toplamı",
                    icon: "bx-money", color: "info",
                    link: "/ecommerce-orders",
                  },
                ].map((s, i) => (
                  <Col key={i} xs={6} md={3}>
                    <Card className="mini-stats-wid mb-3">
                      <CardBody>
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-1 font-size-13">{s.title}</p>
                            <h4 className="mb-0">
                              {stats.isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                            </h4>
                            <p className="text-muted mb-0 font-size-11 mt-1">{s.sub}</p>
                          </div>
                          <div className={`avatar-sm rounded-circle bg-${s.color} align-self-center ms-2`}>
                            <span className={`avatar-title rounded-circle bg-${s.color}`}>
                              <i className={`bx ${s.icon} font-size-20`} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row>
                <Col xl={4}>
                  <WelcomeComp />
                  <MonthlyEarning />
                </Col>
                <Col xl={8}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-3">{new Date().getFullYear()} — Aylık İlan Sayım</CardTitle>
                      {!chartReady ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: 260 }}>
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <ReactApexChart
                          key={`user-chart-${(chartSeries[0]?.data || []).join(",")}`}
                          options={chartOptions}
                          series={chartSeries}
                          type="bar"
                          height={260}
                          className="apex-charts"
                        />
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col xl={6}>
                  <ActivityComp />
                </Col>
                <Col xl={6}>
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle className="mb-0">Son Tekliflerim</CardTitle>
                        <Link to="/ecommerce-products" className="text-primary font-size-13">
                          Tümü <i className="mdi mdi-arrow-right" />
                        </Link>
                      </div>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Teklif Tutarı</th>
                              <th>Tarih</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {myRecentOffers.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-3">
                                  Henüz teklif yok
                                </td>
                              </tr>
                            ) : myRecentOffers.map(t => (
                              <tr key={t.ID}>
                                <td className="text-muted">#{t.ID}</td>
                                <td className="fw-bold text-primary">
                                  {Number(t.TEKLIF_TUTARI || 0).toLocaleString("tr-TR")} ₺
                                </td>
                                <td className="text-muted font-size-12">
                                  {t.TEKLIF_TARIHI ? new Date(t.TEKLIF_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                                <td>{teklifBadge(t.DURUM)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-3">Son Ödemelerim</CardTitle>
                      <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0" size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Referans</th>
                              <th>Tutar</th>
                              <th>Ödeme Tipi</th>
                              <th>Tarih</th>
                              <th>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentPayments.length === 0 ? (
                              <tr><td colSpan={5} className="text-center text-muted py-3">Ödeme kaydı yok</td></tr>
                            ) : recentPayments.map(p => (
                              <tr key={p.ID}>
                                <td className="text-muted">{p.REFERANS_NO || `#${p.ID}`}</td>
                                <td className="fw-bold text-success">
                                  {Number(p.ODEME_MIKTARI).toLocaleString("tr-TR")} ₺
                                </td>
                                <td><Badge color="light" className="text-dark border">{p.ODEME_TIP || "-"}</Badge></td>
                                <td className="text-muted font-size-12">
                                  {p.ODEME_TARIHI ? new Date(p.ODEME_TARIHI).toLocaleDateString("tr-TR") : "-"}
                                </td>
                                <td>
                                  <Badge color={p.ODEME_DURUMU?.toLowerCase().includes("tamamland") ? "success" : "warning"}>
                                    {p.ODEME_DURUMU || "Bekliyor"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Dashboard);
